import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { toast } from 'sonner';
import { ReportConfig } from '../data/reports';
export type ExportStatus = 'queued' | 'processing' | 'ready' | 'synced';
export type ExportDestination = 'download' | 'drive';
export interface ExportJob {
  id: string;
  reportName: string;
  format: 'xlsx' | 'csv';
  destination: ExportDestination;
  status: ExportStatus;
  requestedAt: string;
  rowCount: number;
}
export interface SavedTemplate {
  id: string;
  name: string;
  description: string;
  config: ReportConfig;
  updatedAt: string;
  createdBy: string;
}
export type Theme = 'light' | 'dark';
interface AppContextValue {
  exports: ExportJob[];
  queueExport: (reportName: string, rowCount: number, format: 'xlsx' | 'csv') => void;
  clearExport: (id: string) => void;
  driveSynced: boolean;
  setDriveSynced: (v: boolean) => void;
  templates: SavedTemplate[];
  saveTemplate: (name: string, description: string, config: ReportConfig, existingId?: string) => string;
  deleteTemplate: (id: string) => void;
  queuePanelOpen: boolean;
  setQueuePanelOpen: (v: boolean) => void;
  theme: Theme;
  toggleTheme: () => void;
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;
}
const AppContext = createContext<AppContextValue | null>(null);
const seedTemplates: SavedTemplate[] = [{
  id: 'tpl-0',
  name: 'Appointments + Lab Results',
  description: 'Joined view: each appointment with its matched lab result and turnaround.',
  config: {
    datasetId: 'appointments',
    joinId: 'appts-results',
    columns: ['id', 'participant', 'company', 'status', 'results.result', 'results.turnaroundHrs'],
    filters: [],
    groupBy: null,
    metricField: null,
    metricFn: 'count',
    chartType: 'table'
  },
  updatedAt: '2026-07-09',
  createdBy: 'Robert M.'
}, {
  id: 'tpl-1',
  name: 'Q2 No-Show Analysis',
  description: 'Appointments filtered to no-shows, grouped by collection site.',
  config: {
    datasetId: 'appointments',
    columns: ['id', 'participant', 'company', 'collectionSite', 'apptDate'],
    filters: [{
      field: 'status',
      operator: 'equals',
      value: 'No Show'
    }],
    groupBy: 'collectionSite',
    metricField: null,
    metricFn: 'count',
    chartType: 'bar'
  },
  updatedAt: '2026-07-06',
  createdBy: 'Robert M.'
}, {
  id: 'tpl-2',
  name: 'DOT Turnaround Watch',
  description: 'Average turnaround hours for DOT urine tests, by lab.',
  config: {
    datasetId: 'results',
    columns: ['lab', 'testCategory', 'turnaroundHrs'],
    filters: [{
      field: 'testCategory',
      operator: 'contains',
      value: 'DOT'
    }],
    groupBy: 'lab',
    metricField: 'turnaroundHrs',
    metricFn: 'avg',
    chartType: 'bar'
  },
  updatedAt: '2026-07-02',
  createdBy: 'Robert M.'
}];
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [exports, setExports] = useState<ExportJob[]>([{
    id: 'exp-seed-1',
    reportName: 'Master Results Report',
    format: 'xlsx',
    destination: 'download',
    status: 'ready',
    requestedAt: 'Yesterday, 4:12 PM',
    rowCount: 20
  }]);
  const [driveSynced, setDriveSynced] = useState(false);
  const [templates, setTemplates] = useState<SavedTemplate[]>(seedTemplates);
  const [queuePanelOpen, setQueuePanelOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>('dark');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleTheme = useCallback(() => setTheme(prev => prev === 'dark' ? 'light' : 'dark'), []);
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(t => window.clearTimeout(t)), []);
  const queueExport = useCallback((reportName: string, rowCount: number, format: 'xlsx' | 'csv') => {
    const id = `exp-${Date.now()}`;
    const destination: ExportDestination = driveSynced ? 'drive' : 'download';
    const job: ExportJob = {
      id,
      reportName,
      format,
      destination,
      status: 'queued',
      requestedAt: 'Just now',
      rowCount
    };
    setExports(prev => [job, ...prev]);
    toast(destination === 'drive' ? `"${reportName}" queued — will sync to Google Drive` : `"${reportName}" added to your export queue`, {
      description: `${rowCount} rows · ${format.toUpperCase()}`
    });
    timers.current.push(window.setTimeout(() => {
      setExports(prev => prev.map(e => e.id === id ? {
        ...e,
        status: 'processing'
      } : e));
    }, 1200));
    timers.current.push(window.setTimeout(() => {
      setExports(prev => prev.map(e => e.id === id ? {
        ...e,
        status: destination === 'drive' ? 'synced' : 'ready'
      } : e));
      toast.success(destination === 'drive' ? `"${reportName}" synced to Google Drive` : `"${reportName}" is ready to download`);
    }, 4200));
  }, [driveSynced]);
  const clearExport = useCallback((id: string) => {
    setExports(prev => prev.filter(e => e.id !== id));
  }, []);
  const saveTemplate = useCallback((name: string, description: string, config: ReportConfig, existingId?: string) => {
    const today = new Date().toISOString().slice(0, 10);
    if (existingId) {
      setTemplates(prev => prev.map(t => t.id === existingId ? {
        ...t,
        name,
        description,
        config,
        updatedAt: today
      } : t));
      toast.success(`Template "${name}" updated`);
      return existingId;
    }
    const id = `tpl-${Date.now()}`;
    setTemplates(prev => [{
      id,
      name,
      description,
      config,
      updatedAt: today,
      createdBy: 'Robert M.'
    }, ...prev]);
    toast.success(`Template "${name}" saved`);
    return id;
  }, []);
  const deleteTemplate = useCallback((id: string) => {
    setTemplates(prev => prev.filter(t => t.id !== id));
    toast('Template deleted');
  }, []);
  return <AppContext.Provider value={{
    exports,
    queueExport,
    clearExport,
    driveSynced,
    setDriveSynced,
    templates,
    saveTemplate,
    deleteTemplate,
    queuePanelOpen,
    setQueuePanelOpen,
    theme,
    toggleTheme,
    mobileNavOpen,
    setMobileNavOpen
  }} data-id="element-202">
      {children}
    </AppContext.Provider>;
}
export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}